import { KText } from '@/ui-library/components';
import {
  CreditedTo,
  DepartmentName,
  Jobname,
  NameCell,
  OfficeNames,
  RejectionReason,
  RejectionStage,
  RejectionType,
  RejectedBy,
  SourceName,
  TimeToReject,
} from '../cells/RejectionDrilldownCells';

const renderDrilldownHeader = (title: string) => () => (
  <KText size="sm-medium" color="secondary-900" textTransform="none">
    {title}
  </KText>
);

export const useRejectionDrilldownConfig = ({
  drilldownHeading = 'Rejections',
  onSort: _onSort = () => {},
  currentSort: _currentSort = {},
}) => {
  const getColumnConfig = () => {
    const columnsMap = {
      applicantName: {
        title: 'Applicant name',
        columnConfig: false,
        Header: renderDrilldownHeader('Applicant name'),
        minWidth: 200,
        accessor: 'candidate_name',
        sort: true,
        Cell: ({ row }) => NameCell(row),
      },
      timeToReject: {
        title: 'Time to reject',
        Header: renderDrilldownHeader('Time to reject'),
        minWidth: 200,
        accessor: 'time_to_reject',
        sort: true,
        Cell: ({ row }) => TimeToReject(row),
      },
      rejectionType: {
        title: 'Rejection type',
        Header: renderDrilldownHeader('Rejection type'),
        minWidth: 160,
        accessor: 'rejection_type',
        sort: true,
        Cell: ({ row }) => RejectionType(row),
      },
      rejectionReason: {
        title: 'Rejection reason',
        Header: renderDrilldownHeader('Rejection reason'),
        minWidth: 200,
        accessor: 'rejection_reason',
        sort: true,
        Cell: ({ row }) => RejectionReason(row),
      },
      sourceName: {
        title: 'Source',
        Header: renderDrilldownHeader('Source'),
        minWidth: 200,
        accessor: 'appl_contact_source',
        sort: true,
        Cell: ({ row }) => SourceName(row),
      },
      creditedTo: {
        title: 'Credited to',
        Header: renderDrilldownHeader('Credited to'),
        minWidth: 200,
        accessor: 'appl_credited_to',
        sort: true,
        Cell: ({ row }) => CreditedTo(row),
      },
      rejectedBy: {
        title: 'Rejected by',
        Header: renderDrilldownHeader('Rejected by'),
        minWidth: 180,
        accessor: 'rejected_by',
        sort: true,
        Cell: ({ row }) => RejectedBy(row),
      },
      jobName: {
        title: 'Job name',
        Header: renderDrilldownHeader('Job name'),
        minWidth: 200,
        accessor: 'job_name',
        sort: true,
        Cell: ({ row }) => Jobname(row),
      },
      rejectionStage: {
        title: 'Rejection stage',
        Header: renderDrilldownHeader('Rejection stage'),
        minWidth: 160,
        accessor: 'rejection_stage',
        sort: true,
        Cell: ({ row }) => RejectionStage(row),
      },
      department: {
        title: 'Department',
        Header: renderDrilldownHeader('Department'),
        minWidth: 200,
        accessor: 'department_name',
        sort: true,
        Cell: ({ row }) => DepartmentName(row),
      },
      officeNames: {
        title: 'Offices',
        Header: renderDrilldownHeader('Offices'),
        minWidth: 220,
        accessor: 'office_name',
        sort: true,
        Cell: ({ row }) => OfficeNames(row),
      },
    };

    const fullColumns = [
      columnsMap.applicantName,
      columnsMap.timeToReject,
      columnsMap.rejectionType,
      columnsMap.rejectionReason,
      columnsMap.sourceName,
      columnsMap.creditedTo,
      columnsMap.rejectedBy,
      columnsMap.jobName,
      columnsMap.rejectionStage,
      columnsMap.department,
      columnsMap.officeNames,
    ];

    const columnsWithoutRejectionType = [
      columnsMap.applicantName,
      columnsMap.timeToReject,
      columnsMap.rejectionReason,
      columnsMap.sourceName,
      columnsMap.creditedTo,
      columnsMap.rejectedBy,
      columnsMap.jobName,
      columnsMap.rejectionStage,
      columnsMap.department,
      columnsMap.officeNames,
    ];

    const columnsWithoutSource = [
      columnsMap.applicantName,
      columnsMap.timeToReject,
      columnsMap.rejectionType,
      columnsMap.rejectionReason,
      columnsMap.creditedTo,
      columnsMap.rejectedBy,
      columnsMap.jobName,
      columnsMap.rejectionStage,
      columnsMap.department,
      columnsMap.officeNames,
    ];

    switch (drilldownHeading) {
      case 'Rejected Candidates':
      case 'Rejected candidates':
      case 'Time to reject':
      case 'Rejected Trend':
      case 'Rejected trend':
      case 'Rejection by stage':
      case 'Rejections':
      case 'Average time to reject':
        return fullColumns;

      case 'We rejected them':
      case 'They rejected us':
      case 'Others':
        return columnsWithoutRejectionType;

      case 'Rejections by Source (on application)':
      case 'Rejections by source (on application)':
        return columnsWithoutSource;

      default:
        return fullColumns;
    }
  };

  return { columnConfig: getColumnConfig() };
};
